import { getVoiceShareUrl } from "@/app/_data/voices";
import { appUrls } from "@/constants";
import { env } from "@/env";
import { capitalize } from "@utilities/text";

/** Cap the optional similar-voices lookup so a slow request can't gate the classifier result. */
const SIMILAR_VOICES_TIMEOUT_MS = 15_000;

export type AISpeechClassifierResponseModel = {
  probability: number;
};

/**
 * Classifies whether an uploaded audio sample is AI-generated speech.
 *
 * Pass `signal` to cancel the request (e.g. when the user resets or uploads a new sample) —
 * the underlying fetch is aborted, not just ignored.
 */
export const classifyAISpeech = async (file: File, signal?: AbortSignal): Promise<AISpeechClassifierResponseModel> => {
  const formData = new FormData();
  formData.append("file", file);
  const url = `${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/moderation/ai-speech-classification`;
  const response = await fetch(url, {
    method: "POST",
    body: formData,
    signal,
  });

  if (response.status !== 200) {
    const body = await response.json();
    throw new Error(body?.detail?.message || "Failed to classify AI speech, please try again later");
  }
  const body: AISpeechClassifierResponseModel = await response.json();
  return body;
};

/** Subset of the xi `LibraryVoiceResponseModel` we render in the similar-voices list. */
interface LibraryVoiceItem {
  voice_id: string;
  name: string;
  descriptive?: string | null;
  category?: string | null;
  use_case?: string | null;
  preview_url?: string | null;
  image_url?: string | null;
  cloned_by_count?: number | null;
  usage_character_count_1y?: number | null;
}

interface SimilarVoicesResponse {
  voices: LibraryVoiceItem[];
}

/** Slim view model for a single similar-voice row. */
export interface SimilarVoice {
  voiceId: string;
  name: string;
  /** Humanized descriptor from API fields, e.g. "Social media" — null when unavailable. */
  descriptor: string | null;
  /** Famous/celebrity voice — rendered with a localized "Iconic Voice" descriptor. */
  isIconic: boolean;
  /** Formatted usage/clone count, e.g. "109.2K" — null when unavailable. */
  count: string | null;
  previewUrl: string | null;
  imageUrl: string | null;
  /** Deep link that opens this voice in the app. */
  useVoiceUrl: string;
}

/** One decimal place, dropping a trailing ".0" (e.g. 109.2, 5). */
const trimToOneDecimal = (n: number) => n.toFixed(1).replace(/\.0$/, "");

function formatCount(value: number | null | undefined) {
  if (!value || value <= 0) return null;
  if (value >= 1_000_000) return `${trimToOneDecimal(value / 1_000_000)}M`;
  if (value >= 1_000) return `${trimToOneDecimal(value / 1_000)}K`;
  return String(value);
}

function humanize(value: string | null | undefined) {
  if (!value) return null;
  const text = value.replace(/[_-]+/g, " ").trim();
  return text ? capitalize(text) : null;
}

/** Humanized descriptor from API fields. The iconic case is localized at render time. */
function getDescriptor(voice: LibraryVoiceItem) {
  return humanize(voice.use_case) ?? humanize(voice.descriptive) ?? humanize(voice.category);
}

function toSimilarVoice(voice: LibraryVoiceItem): SimilarVoice {
  return {
    voiceId: voice.voice_id,
    name: voice.name,
    descriptor: getDescriptor(voice),
    isIconic: voice.category === "famous",
    // `||` (not `??`) so a 0 clone-count falls back to usage instead of rendering as "no count".
    count: formatCount(voice.cloned_by_count || voice.usage_character_count_1y),
    previewUrl: voice.preview_url ?? null,
    imageUrl: voice.image_url ?? null,
    useVoiceUrl: getVoiceShareUrl({ isVoiceLibraryVoice: true, voiceId: voice.voice_id }) ?? appUrls.voiceLibrary,
  };
}

/**
 * Fetches shared voices similar to the uploaded sample from the anonymous
 * (no-auth) xi endpoint. Used by the public AI Speech Classifier demo.
 *
 * The endpoint is hCaptcha-gated (like the other anonymous marketing demos), so a
 * token from `getCaptchaToken` must be passed and is sent as a multipart form field.
 *
 * Pass `signal` to cancel the request; it's combined with an internal request timeout
 * so the fetch aborts on whichever fires first.
 */
export const fetchSimilarVoices = async (
  file: File,
  hcaptchaToken: string,
  signal?: AbortSignal,
): Promise<SimilarVoice[]> => {
  const formData = new FormData();
  formData.append("audio_file", file);
  formData.append("hcaptcha_token", hcaptchaToken);
  const url = `${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/similar-voices/anonymous`;
  const timeout = AbortSignal.timeout(SIMILAR_VOICES_TIMEOUT_MS);
  const response = await fetch(url, {
    method: "POST",
    body: formData,
    signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
  });

  if (response.status !== 200) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail?.message || "Failed to fetch similar voices");
  }

  const body: SimilarVoicesResponse = await response.json();
  return (body.voices ?? []).map(toSimilarVoice);
};
